import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from './googleMapsConfig';

const API_KEY = GOOGLE_MAPS_API_KEY;
const LIBRARIES = GOOGLE_MAPS_LIBRARIES.join(',');
const SCRIPT_SELECTOR = 'script[src*="maps.googleapis.com/maps/api/js"]';
const LOAD_TIMEOUT_MS = 30000;

type GoogleMapsWindow = Window & {
  google?: {
    maps?: {
      places?: unknown;
    };
  };
};

let loadPromise: Promise<void> | null = null;

function getGoogleMapsWindow(): GoogleMapsWindow {
  return window as GoogleMapsWindow;
}

function isGoogleMapsPlacesReady(): boolean {
  return Boolean(getGoogleMapsWindow().google?.maps?.places);
}

function waitForGoogleMapsReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isGoogleMapsPlacesReady()) {
      resolve();
      return;
    }

    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      if (isGoogleMapsPlacesReady()) {
        window.clearInterval(interval);
        resolve();
        return;
      }

      if (Date.now() - startedAt > LOAD_TIMEOUT_MS) {
        window.clearInterval(interval);
        reject(new Error('Google Maps load timeout'));
      }
    }, 50);
  });
}

export function isGoogleMapsLoaded(): boolean {
  return isGoogleMapsPlacesReady();
}

export function loadGoogleMaps(): Promise<void> {
  if (isGoogleMapsPlacesReady()) {
    return Promise.resolve();
  }

  if (loadPromise) {
    return loadPromise;
  }

  const existingScript = document.querySelector(SCRIPT_SELECTOR);
  if (existingScript) {
    loadPromise = waitForGoogleMapsReady();
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const callbackName = `__googleMapsInit_${Date.now()}`;
    const googleWindow = window as unknown as Record<string, (() => void) | undefined>;
    googleWindow[callbackName] = () => {
      delete googleWindow[callbackName];
      resolve();
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=${LIBRARIES}&loading=async&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      delete googleWindow[callbackName];
      reject(new Error('Google Maps failed to load'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}
